import { v4 as uuidv4 } from "uuid";

import "./middleware.js";

let pageLoadId = uuidv4();

const ONE_YEAR = 60 * 60 * 24 * 365 * 1;

const USER_ID_VERSION = "v2";
const SESSION_ID_VERSION = "v2";

function productsTransformer(data) {
  data = JSON.parse(data);

  const results = (data?.results ?? []).map(result => {
    const variants = JSON.parse(result.variants.replace(/&quot;/g, '"'));
    const title = result.title.replace(/&amp;/g, "&");

    return { ...result, title, selectedVariant: variants[0] };
  });

  return { ...data, results };
}

function recommendationTransformer(data) {
  data = JSON.parse(data);

  const rawResponse = Array.isArray(data) ? data[0] : data;
  const responseId = rawResponse?.responseId ?? null;
  const results = (rawResponse?.results ?? []).map(
    ({ mappings, attributes }) => ({
      ...attributes,
      mappings, // To send back in recommendation tracking
      id: mappings.core.uid,
      recId: mappings.core.uid,
      options: JSON.parse(attributes.options ?? null) ?? [],
      metafields: JSON.parse(attributes.metafields),
      color_swatches: JSON.parse(attributes.color_swatches),
      variants: JSON.parse(attributes.variants).map(variant => ({
        ...variant,
        compareAtPrice: variant.compare_at_price,
        productId: variant.product_id,
        selectedOptions: variant.selected_options,
      })),
    })
  );

  return { results, responseId };
}

export default ({ app, isDev, $config }, inject) => {
  const searchspring = {
    setPageLoadId() {
      pageLoadId = uuidv4();
    },
    getIsuid() {
      return app.$cookies.get("_isuid");
    },
    setIsuid() {
      let isuid = app.$cookies.get("_isuid");

      if (!isuid) {
        isuid = uuidv4();
        app.$cookies.set("_isuid", isuid, this.getCookieOptions());
      }
    },
    getuserId() {
      if (process.server) {
        return app.$cookies.get("ssUserId");
      }

      const userId = app.$cookies.get("ssUserId");
      const version = app.$cookies.get("ssUserIdVersion");

      if (userId && version === USER_ID_VERSION) {
        return userId;
      }

      const newUserId = uuidv4();

      app.$cookies.set("ssUserId", newUserId, this.getCookieOptions());
      app.$cookies.set(
        "ssUserIdVersion",
        USER_ID_VERSION,
        this.getCookieOptions({ maxAge: ONE_YEAR })
      );

      return newUserId;
    },
    setUserId() {
      this.getuserId();
    },
    getSessionIdNamespace() {
      if (process.server) {
        return app.$cookies.get("ssSessionIdNamespace");
      }

      const sessionId = app.$cookies.get("ssSessionIdNamespace");
      const version = app.$cookies.get("ssSessionIdVersion");

      if (sessionId && version === SESSION_ID_VERSION) {
        return sessionId;
      }

      const newSessionId = uuidv4();

      app.$cookies.set(
        "ssSessionIdNamespace",
        newSessionId,
        this.getCookieOptions({ maxAge: null })
      );

      app.$cookies.set(
        "ssSessionIdVersion",
        SESSION_ID_VERSION,
        this.getCookieOptions({ maxAge: ONE_YEAR })
      );

      return newSessionId;
    },
    setSessionIdNamespace() {
      this.getSessionIdNamespace();
    },
    getViewedProducts() {
      let viewedProducts = app.$cookies.get("ssViewedProducts");

      return viewedProducts && typeof viewedProducts === "string"
        ? viewedProducts
        : "";
    },
    setViewedProducts(sku) {
      const viewedProducts = this.getViewedProducts();
      const products = viewedProducts.includes(sku)
        ? viewedProducts
        : [sku, viewedProducts].filter(Boolean).join(",");

      app.$cookies.set(
        "ssViewedProducts",
        products,
        this.getCookieOptions({ maxAge: ONE_YEAR * 7 })
      );
    },
    getCookieOptions({ maxAge = ONE_YEAR, path = "/" } = {}) {
      return {
        ...(maxAge && { maxAge }),
        ...{ path },
        ...(isDev
          ? {}
          : { sameSite: "lax", domain: $config.domainName, secure: true }),
      };
    },
    getProfileContext() {
      return {
        pageLoadId,
        sessionId: this.getSessionIdNamespace(),
        userId: this.getuserId(),
        website: { trackingCode: "g28s7j" },
      };
    },
    getBeaconContext({ pageUrl = "" } = {}) {
      const customerEmail = app.store?.state?.wishlist?.customer?.email;
      return {
        timestamp: new Date().toISOString(),
        pageUrl:
          pageUrl ||
          (typeof window !== "undefined" ? window.location.href : ""),
        userId: this.getuserId(),
        sessionId: this.getSessionIdNamespace(),
        pageLoadId,
        initiator: "snooze",
        ...(customerEmail && { shopperId: customerEmail }),
        currency: { code: "AUD" },
      };
    },
    callBeacon2Api(module, event, data = {}) {
      if (
        !app.$cookieConsent.isConsented("analytics")
      ) {
        return Promise.resolve({ ok: true, skipped: true });
      }

      const siteId = "g28s7j";
      const baseUrl = "https://analytics.searchspring.net/beacon/v2";
      const url = `${baseUrl}/${siteId}/${module}/${event}`;
      const payload = JSON.stringify({
        context: this.getBeaconContext(),
        data,
      });

      // Use native fetch with 'text/plain' to skip CORS preflight (OPTIONS) requests and bypass axios interceptors
      if (typeof window !== "undefined" && typeof window.fetch === "function") {
        // Browser keepalive quota is ~64 KiB. Fallback to non-keepalive fetch if oversized.
        const canKeepAlive = payload.length < 60000;

        return window
          .fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "text/plain",
            },
            body: payload,
            keepalive: canKeepAlive,
            mode: "cors",
          })
          .then(response => {
            if (!response.ok && isDev) {
              console.error(
                `[Searchspring Beacon] HTTP error ${response.status} on ${module}/${event}`
              );
            }
            return { ok: response.ok, status: response.status };
          })
          .catch(err => {
            // Silently absorb ad-blocker / network errors without alerting Bugsnag or crashing UI
            if (isDev) {
              console.debug(
                "[Searchspring Beacon] Blocked or network failure:",
                err?.message || err
              );
            }
            return { ok: false, error: err };
          });
      }

      return Promise.resolve({ ok: true, skipped: true });
    },
    getPersonalizationParams(skipParams = "") {
      const lineSkus = app.store.getters["bag/lineSkus"];
      const customerEmail = app.store.state.wishlist.customer?.email;
      const skipList = skipParams
        ? skipParams.split(",").map(p => p.trim())
        : [];

      const baseParams = {
        cart: lineSkus.length === 0 ? "" : lineSkus.join(","),
        lastViewed: this.getViewedProducts(),
        shopper: customerEmail || undefined,
      };

      // Dynamically remove fields present in skipParams
      skipList.forEach(key => {
        if (key in baseParams) {
          delete baseParams[key];
        }
      });

      return baseParams;
    },
    getUserTrackingParams() {
      return {
        sessionId: this.getSessionIdNamespace(),
        userId: this.getuserId(),
        pageLoadId,
        domain: `${$config.appEndPoint}${app.context.route.fullPath}`,
      };
    },
    recommend({ tag, limit, sku, skipParams }) {
      console.assert(tag, "tag is required");

      let searchParams = new URLSearchParams({
        tags: tag,
        limits: limit ?? 12,
        ...(sku && { products: sku }),
        ...this.getPersonalizationParams(skipParams),
      });

      const siteId = "g28s7j";
      const url = `https://${siteId}.a.searchspring.io/boost/${siteId}/recommend?${searchParams}`;

      return app.$axios.get(url, {
        transformResponse: [recommendationTransformer],
      });
    },
    blogSearch({ query, page = 1, pageSize, tags = [] } = {}) {
      const siteId = "wf2lv2";
      const bgfilters = tags.reduce(
        (acc, tag) => ({ ...acc, "bgfilter.tags": tag }),
        {}
      );

      let searchParams = new URLSearchParams({
        ...(query && { q: query }),
        page,
        resultsPerPage: pageSize,
        siteId,
        resultsFormat: "json",
        ...bgfilters,
        ...this.getUserTrackingParams(),
        ...this.getPersonalizationParams(),
      });

      const url = `https://${siteId}.a.searchspring.io/api/search/search.json?${searchParams}`;
      return app.$axios.get(url);
    },
    suggest({ siteId, query }) {
      let searchParams = new URLSearchParams({
        siteId,
        suggestionCount: 5,
        query,
        integratedSpellCorrection: true,
      });

      const url = `https://${siteId}.a.searchspring.io/api/suggest/query?${searchParams}
      `;
      return app.$axios.get(url);
    },
    async autocomplete({
      siteId,
      query,
      resultsPerPage = 3,
      responseTransformers = [data => JSON.parse(data)],
    }) {
      let searchParams = new URLSearchParams({
        siteId,
        resultsPerPage,
        q: query,
        ...this.getUserTrackingParams(),
        ...this.getPersonalizationParams(),
        resultsFormat: "json",
      });

      const url = `https://${siteId}.a.searchspring.io/api/search/autocomplete.json?${searchParams}`;
      const response = await app.$axios.get(url, {
        transformResponse: responseTransformers,
      });

      return response;
    },
    async collectionsAutocomplete(query) {
      const siteId = "bkqbz7";
      const suggenstions = await this.suggest({ siteId, query });
      const suggestion = suggenstions.data?.suggested?.text ?? query;
      return this.autocomplete({
        siteId,
        query: suggestion,
        resultsPerPage: 5,
      });
    },
    async productsAutocomplete(query, resultsPerPage = 3) {
      const siteId = "g28s7j";
      const suggestionResponse = await this.suggest({ siteId, query });

      const suggestions = [
        ...new Set([
          suggestionResponse.data?.suggested?.text,
          ...suggestionResponse.data?.alternatives.map(alt => alt.text),
        ]),
      ];

      const { data } = await this.autocomplete({
        siteId,
        query: suggestions?.[0] ?? query,
        responseTransformers: [productsTransformer],
        resultsPerPage,
      });

      return { data, suggestions };
    },
    async blogsAutocomplete(query, resultsPerPage = 3) {
      const siteId = "wf2lv2";
      const suggenstions = await this.suggest({ siteId, query });
      const suggestion = suggenstions.data?.suggested?.text ?? query;
      return this.autocomplete({ siteId, query: suggestion, resultsPerPage });
    },
    async getTrendingQueries(limit = 6) {
      const siteId = "g28s7j";

      const url = `https://${siteId}.a.searchspring.io/api/suggest/trending?siteId=${siteId}&limit=${limit}`;

      try {
        const { data } = await app.$axios.get(url);

        return (data?.trending?.queries ?? []).map(item => item.searchQuery);
      } catch (e) {
        return [];
      }
    },
  };

  inject("search", searchspring);
};
